import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

/**
 * Axios instance for IP whitelist API calls
 * - Base URL: `${API_BASE_URL}/api/v1`
 * - JSON content type by default
 */
// Create axios instance for IP API calls
const ipAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor
 * - Attaches available auth token to outgoing requests
 * - Logs a warning when no token is found (helpful during development)
 */
// Add request interceptor to include auth token
ipAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No auth token found in storage for ipAPI request');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor
 * - Handles 401 Unauthorized responses by clearing auth storage and
 *   redirecting to the login route. This keeps a consistent UX when the
 *   backend invalidates tokens.
 */
// Add response interceptor to handle auth errors
ipAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);


/**
 * IpService
 * - Wrapper around `ipAPI` for CRUD operations related to the IP whitelist
 * - Methods throw on network error; callers should handle/display errors
 */
class IpService {
  /**
   * Get all IP addresses
   * @returns {Promise} Array of IP addresses
   */

  /**
   * Get IP whitelist with pagination, filtering and search
   * @param {Object} options - Query options
   * @param {number} [options.skip=0]
   * @param {number} [options.limit=20]
   * @param {boolean} [options.active_only=false]
   * @param {string} [options.search='']
   * @returns {Promise} Paginated list of whitelisted IPs
   */
  async getIpWhitelist({ skip = 0, limit = 20, active_only = false, search = '' } = {}) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('skip', skip);
      queryParams.append('limit', limit);
      if (active_only) queryParams.append('active_only', active_only);
      if (search) queryParams.append('search', search);
      const response = await ipAPI.get(`/ip-whitelist?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching IP whitelist:', error);
      const errorMsg = extractApiError(error);
      const enhancedError = new Error(errorMsg);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  /**
   * Get IP address by ID
   * @param {string} id - IP address ID
   * @returns {Promise} IP address object
   */
  async getIpAddressById(id) {
    try {
      const response = await ipAPI.get(`ip-whitelist/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching IP address:', error);
      const errorMsg = extractApiError(error);
      const enhancedError = new Error(errorMsg);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  /**
   * Add a single IP address
   * @param {Object} ipData - IP address data
   * @returns {Promise} Created IP address object
   */
  async addIpAddress(ipData) {
    try {
      const response = await ipAPI.post('ip-whitelist', ipData);
      return response.data;
    } catch (error) {
      console.error('Error adding IP address:', error);
      const errorMsg = extractApiError(error);
      const enhancedError = new Error(errorMsg);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  /**
   * Add multiple IP addresses in bulk
   * @param {Object} bulkData - Bulk IP data with ip_addresses array and description
   * @returns {Promise} Result of bulk creation
   */
  async addBulkIpAddresses(bulkData) {
    try {
      const response = await ipAPI.post('ip-whitelist/bulk', bulkData);
      return response.data;
    } catch (error) {
      console.error('Error adding bulk IP addresses:', error);
      const errorMsg = extractApiError(error);
      const enhancedError = new Error(errorMsg);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  /**
   * Update an IP address
   * @param {string} id - IP address ID
   * @param {Object} ipData - Updated IP address data
   * @returns {Promise} Updated IP address object
   */
  async updateIpAddress(id, ipData) {
    try {
      const response = await ipAPI.put(`ip-whitelist/${id}`, ipData);
      return response.data;
    } catch (error) {
      console.error('Error updating IP address:', error);
      const errorMsg = extractApiError(error);
      const enhancedError = new Error(errorMsg);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  /**
   * Delete an IP address
   * @param {string} id - IP address ID
   * @returns {Promise} Deletion result
   */
  async deleteIpAddress(id) {
    if (!id && id !== 0) {
      const err = new Error('deleteIpAddress called without a valid id');
      console.error(err.message, { id });
      throw err;
    }

    try {
      await ipAPI.delete(`ip-whitelist/${id}`);
      return { success: true };
    } catch (error) {
      console.error('Error deleting IP address:', error);
      const errorMsg = extractApiError(error);
      const enhancedError = new Error(errorMsg);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  /**
   * Activate an IP address
   * @param {string} id - IP address ID
   * @returns {Promise} Updated IP address object
   */
  async activateIpAddress(id) {
    if (!id && id !== 0) {
      const err = new Error('activateIpAddress called without a valid id');
      console.error(err.message, { id });
      throw err;
    }

    try {
      const response = await ipAPI.patch(`ip-whitelist/${id}/activate`);
      return response.data;
    } catch (error) {
      console.error('Error activating IP address:', error);
      const errorMsg = extractApiError(error);
      const enhancedError = new Error(errorMsg);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }

  /**
   * Deactivate an IP address
   * @param {string} id - IP address ID
   * @returns {Promise} Updated IP address object
   */
  async deactivateIpAddress(id) {
    if (!id && id !== 0) {
      const err = new Error('deactivateIpAddress called without a valid id');
      console.error(err.message, { id });
      throw err;
    }

    try {
      const response = await ipAPI.patch(`ip-whitelist/${id}/deactivate`);
      return response.data;
    } catch (error) {
      console.error('Error deactivating IP address:', error);
      const errorMsg = extractApiError(error);
      const enhancedError = new Error(errorMsg);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  }
}

const ipService = new IpService();

export default ipService;
